/**
 * useSubscription Hook
 * 
 * Subscription, kredi kontrolü ve plan yükseltme mantığını yöneten custom hook.
 * HomePage.js'den ayrıştırıldı.
 */

import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isPersonaStaff } from '@/utils/staff';
import { getPricingPath } from '@/utils/localePaths';
import { getStoredLanguage } from '@/config/languages';

/**
 * useSubscription - Subscription ve kredi yönetimi hook'u
 */
export const useSubscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Modal States
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Credit estimation state
  const [estimatedCredits, setEstimatedCredits] = useState(0);

  /**
   * getRemainingTime - Abonelik kalan süre hesaplama
   */
  const getRemainingTime = useCallback(() => {
    if (!user?.subscription_end_date) return null;
    
    const now = new Date();
    const endDate = new Date(user.subscription_end_date);
    const diff = endDate - now;
    
    if (diff <= 0) return "Süre doldu";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    // Sadece en büyük birim göster
    if (days > 0) {
      return `${days} gün`;
    } else if (hours > 0) {
      return `${hours} saat`;
    } else {
      return `${minutes} dakika`;
    }
  }, [user?.subscription_end_date]);

  /**
   * hasCredits - Kredi var mı kontrolü
   */
  const hasCredits = useMemo(() => {
    if (!user) return false;
    return (user.credits_remaining || 0) > 0;
  }, [user]);

  /**
   * canMakeQuery - Sorgu yapılabilir mi
   */
  const canMakeQuery = useMemo(() => {
    if (!user) return false;
    // Persona Staff için sınırsız
    if (isPersonaStaff(user)) return true;
    // Diğerleri için kredi kontrolü
    return hasCredits;
  }, [user, hasCredits]);

  /**
   * checkCreditsBeforeQuery - Sorgu öncesi kredi kontrolü
   * @returns {boolean} - true ise sorgu yapılabilir, false ise modal açılır
   */
  const checkCreditsBeforeQuery = useCallback((requiredCredits = 1) => {
    if (!user) {
      // Giriş yapılmamış
      return false;
    }
    
    // Persona Staff için sınırsız
    if (isPersonaStaff(user)) {
      return true;
    }
    
    // Kredi kontrolü
    if ((user.credits_remaining || 0) < requiredCredits) {
      setShowQuotaModal(true);
      return false;
    }
    
    return true;
  }, [user]);

  /**
   * handleUpgradeClick - Plan yükseltme tıklaması
   */
  const handleUpgradeClick = useCallback(() => {
    setShowQuotaModal(false);
    navigate(getPricingPath(getStoredLanguage()));
  }, [navigate]);

  /**
   * confirmCreditUsage - Kredi kullanımı onayı
   */
  const confirmCreditUsage = useCallback((credits) => {
    setEstimatedCredits(credits);
    setShowCreditConfirm(true);
  }, []);

  /**
   * closeAllModals - Tüm modalları kapat
   */
  const closeAllModals = useCallback(() => {
    setShowQuotaModal(false);
    setShowCreditConfirm(false);
    setShowLogoutConfirm(false);
  }, []);

  /**
   * currentPlan - Mevcut plan bilgisi
   */
  const currentPlan = useMemo(() => {
    if (!user) return null;
    
    return {
      name: user.plan_name || user.plan_type || 'free',
      type: user.plan_type,
      credits: user.credits_remaining || 0,
      endDate: user.subscription_end_date,
      isStaff: isPersonaStaff(user),
      isDunning: user.subscription_status === 'dunning',
      gracePeriodEnd: user.grace_period_end
    };
  }, [user]);

  /**
   * subscriptionStatus - Abonelik durumu
   */
  const subscriptionStatus = useMemo(() => {
    if (!user) return 'guest';
    if (user.subscription_status === 'dunning') return 'dunning';
    if (user.subscription_status === 'expired') return 'expired';
    if (isPersonaStaff(user)) return 'staff';
    if ((user.credits_remaining || 0) > 0) return 'active';
    return 'no_credits';
  }, [user]);

  return {
    // Modal States
    showQuotaModal,
    setShowQuotaModal,
    showCreditConfirm,
    setShowCreditConfirm,
    showLogoutConfirm,
    setShowLogoutConfirm,
    
    // Credit State
    estimatedCredits,
    setEstimatedCredits,
    
    // Computed Values
    hasCredits,
    canMakeQuery,
    currentPlan,
    subscriptionStatus,
    
    // Functions
    getRemainingTime,
    checkCreditsBeforeQuery,
    handleUpgradeClick,
    confirmCreditUsage,
    closeAllModals
  };
};

export default useSubscription;
